import React, { useEffect, useState, useCallback, useRef } from 'react';
import { View, Text, FlatList, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useAuth } from '../../hooks/useAuth';
import { getAuditLogsPage } from '../../services/auditService';
import type { AuditLogsPage } from '../../services/auditService';
import { AuditLog } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

const ACTION_COLORS: Record<string, string> = {
  create: '#16A34A',
  edit: '#2563EB',
  delete: '#DC2626',
  update: '#D97706',
  login: '#7C3AED',
};

const PAGE_SIZE = 50;

export default function AuditLogsScreen() {
  const { appUser } = useAuth();
  const { t } = useLanguage();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const cursorRef = useRef<AuditLogsPage['cursor']>(null);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);

  const isAdmin = appUser?.role === 'admin';

  const load = useCallback(async (reset: boolean = true) => {
    if (reset) {
      loadingMoreRef.current = false;
      setLoading(true);
    } else {
      if (loadingMoreRef.current || !hasMoreRef.current) return;
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }

    try {
      const page = await getAuditLogsPage(PAGE_SIZE, reset ? null : cursorRef.current);
      setLogs(prev => reset ? page.logs : [...prev, ...page.logs]);
      cursorRef.current = page.cursor;
      hasMoreRef.current = page.hasMore;
    } finally {
      loadingMoreRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const refresh = useCallback(() => {
    load(true);
  }, [load]);

  useEffect(() => {
    if (isAdmin) {
      load();
    }
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <View style={styles.center}>
        <Text style={styles.noAccess}>{t('auditLogs.noAccess')}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.innerContent}>
        <View style={styles.header}>
          <Text style={styles.title}>{t('auditLogs.title')}</Text>
          <TouchableOpacity onPress={refresh} style={styles.refreshBtn}>
            <Text style={styles.refreshText}>↻</Text>
          </TouchableOpacity>
        </View>
        {loading ? (
          <ActivityIndicator style={{ marginTop: 40 }} size="large" color="#2563EB" />
        ) : (
          <FlatList
            data={logs}
            keyExtractor={item => item.id}
            renderItem={({ item }) => <AuditLogCard item={item} t={t} />}
            ListEmptyComponent={<Text style={styles.empty}>{t('auditLogs.noLogs')}</Text>}
            ListFooterComponent={loadingMore ? <ActivityIndicator style={styles.footerLoader} color="#2563EB" /> : null}
            onEndReached={() => load(false)}
            onEndReachedThreshold={0.4}
          />
        )}
      </View>
    </View>
  );
}

type TFunc = ReturnType<typeof useLanguage>['t'];

function AuditLogCard({ item, t }: { item: AuditLog; t: TFunc }) {
  const actionColor = ACTION_COLORS[item.action] ?? '#64748B';
  const entityTypeLabel =
    t(`auditLogs.entityTypes.${item.entityType}` as Parameters<TFunc>[0]) ?? item.entityType;
  const actionLabel =
    t(`auditLogs.actions.${item.action}` as Parameters<TFunc>[0]) ?? item.action;

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <View style={[styles.actionBadge, { backgroundColor: actionColor }]}>
          <Text style={styles.actionText}>{actionLabel.toUpperCase()}</Text>
        </View>
        <Text style={styles.entityType}>{entityTypeLabel}</Text>
        <Text style={styles.timestamp}>{item.timestamp.toLocaleString()}</Text>
      </View>
      <Text style={styles.entityId} numberOfLines={1}>ID: {item.entityId}</Text>
      {item.userEmail ? (
        <Text style={styles.userEmail}>{item.userEmail}</Text>
      ) : (
        <Text style={styles.userEmail}>{item.userId}</Text>
      )}
      {item.details ? (
        <Text style={styles.details}>{item.details}</Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  innerContent: { flex: 1, maxWidth: 960, width: '100%', alignSelf: 'center' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  noAccess: { fontSize: 18, color: '#6B7280' },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  title: { fontSize: 22, fontWeight: 'bold', color: '#1E293B' },
  refreshBtn: { padding: 8 },
  refreshText: { fontSize: 20, color: '#2563EB' },
  card: {
    backgroundColor: '#FFF',
    margin: 8,
    borderRadius: 12,
    padding: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
    flexWrap: 'wrap',
  },
  actionBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  actionText: { color: '#FFF', fontWeight: '700', fontSize: 11 },
  entityType: { fontSize: 14, fontWeight: '600', color: '#1E293B', flex: 1 },
  timestamp: { fontSize: 11, color: '#94A3B8' },
  entityId: { fontSize: 11, color: '#94A3B8', marginBottom: 2 },
  userEmail: { fontSize: 12, color: '#64748B', marginBottom: 2 },
  details: { fontSize: 12, color: '#475569', marginTop: 4, fontStyle: 'italic' },
  empty: { textAlign: 'center', color: '#94A3B8', marginTop: 40, fontSize: 16 },
  footerLoader: { marginVertical: 18 },
});
